import { useState, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { HiPlus } from 'react-icons/hi';
import { Tab, Tabs } from '@mui/material';
import AddAllDocumentsForm from './AddAllDocumentsForm';
import AddCustomDocumentForm from './AddCustomDocumentForm';
import AddDocumentPopoverMenu from './AddDocumentPopoverMenu';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Modal, ModalHeader } from 'components/common/Modal';
import { DocumentActionButton } from '../../../ClientView.styled';

const AddDocumentRowButton = styled.button`
    width: min(420px, 100%);
    min-height: 38px;
    padding: 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px dashed rgba(131, 83, 226, 0.5);
    border-radius: 14px;
    background: rgba(131, 83, 226, 0.06);
    color: #6d41d8;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition:
        transform 0.2s ease,
        border-color 0.2s ease,
        background 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(131, 83, 226, 0.72);
        background: rgba(131, 83, 226, 0.1);
        box-shadow: 0 12px 24px rgba(131, 83, 226, 0.12);
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

type AddDocumentPopoverProps = {
    mobile: boolean;
    variant?: 'header' | 'tableRow';
    renderTrigger?: (onClick: () => void) => ReactNode;
};

const TAB_ALL = 0;
const TAB_CHECKLIST = 1;
const TAB_CUSTOM = 2;

const AddDocumentPopover = ({
    mobile,
    variant = 'header',
    renderTrigger,
}: AddDocumentPopoverProps) => {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_ALL);
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setActiveTab(TAB_ALL);
    };

    const subtitles: Record<number, string> = {
        [TAB_ALL]: t('addAllDocumentsSubtitle', {
            lng: language,
            defaultValue: 'Search for a document and select the ones to add to this case.',
        }),
        [TAB_CHECKLIST]: t('addDocumentsModalSubtitle', {
            lng: language,
            defaultValue:
                'Select a country and service to build a document checklist for this case.',
        }),
        [TAB_CUSTOM]: t('addCustomDocumentSubtitle', {
            lng: language,
            defaultValue: 'Create a custom document by providing a name and optional description.',
        }),
    };

    return (
        <>
            {renderTrigger ? (
                renderTrigger(handleClickOpen)
            ) : variant === 'tableRow' ? (
                <AddDocumentRowButton type="button" onClick={handleClickOpen}>
                    <HiPlus />
                    {t('addDocuments', {
                        lng: language,
                        defaultValue: mobile ? 'Add docs' : 'Add documents',
                    })}
                </AddDocumentRowButton>
            ) : (
                <DocumentActionButton type="button" onClick={handleClickOpen}>
                    {mobile
                        ? t('addDocs', { lng: language, defaultValue: 'Add docs' })
                        : t('addDocuments', { lng: language, defaultValue: 'Add documents' })}
                </DocumentActionButton>
            )}

            <Modal
                open={open}
                onClose={handleClose}
                size="md"
                ariaLabel={t('addDocuments', { lng: language, defaultValue: 'Add documents' })}
            >
                <ModalHeader
                    eyebrow={t('documents', { lng: language })}
                    title={t('addDocuments', { lng: language, defaultValue: 'Add documents' })}
                    subtitle={subtitles[activeTab]}
                />
                <Tabs
                    value={activeTab}
                    onChange={(_e, value) => setActiveTab(value)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                        },
                        '& .Mui-selected': { color: '#6025D8' },
                        '& .MuiTabs-indicator': { backgroundColor: '#6025D8' },
                    }}
                >
                    <Tab
                        label={t('allDocuments', {
                            lng: language,
                            defaultValue: 'All documents',
                        })}
                    />
                    <Tab
                        label={t('fromChecklist', {
                            lng: language,
                            defaultValue: 'From Checklist',
                        })}
                    />
                    <Tab
                        label={t('customDocument', {
                            lng: language,
                            defaultValue: 'Custom Document',
                        })}
                    />
                </Tabs>
                {activeTab === TAB_ALL && <AddAllDocumentsForm closeMenu={handleClose} />}
                {activeTab === TAB_CHECKLIST && <AddDocumentPopoverMenu closeMenu={handleClose} />}
                {activeTab === TAB_CUSTOM && (
                    <AddCustomDocumentForm closeMenu={handleClose} mobile={mobile} />
                )}
            </Modal>
        </>
    );
};

export default AddDocumentPopover;
